import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Target, CheckCircle, XCircle } from 'lucide-react';
export function ConsequentialThinkingPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-284" data-mp="101olr0g5zclj">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-285" data-mp="w25xbup4wjdy">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-286" data-mp="r4nwysp6nchk">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-287" data-mp="1er4a69s835y2">
            <span className="w-8 h-px bg-teal" data-id="element-288" data-mp="19tuam1b4ddwi"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-289" data-mp="19t2l98848ceo">
              Capability
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-290" data-mp="3ujtcpu5hgsr7"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-291" data-mp="3ulmotoyv856l">
            Consequential Thinking
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-292" data-mp="2flh3arqv6y8h">
            At SyNeural, consequential thinking refers to the ability to
            understand that actions produce downstream effects, often beyond the
            immediate moment. It is the capacity to think through tradeoffs,
            anticipate second-order effects, recognize how one decision can
            reshape later choices, and evaluate whether a short-term gain may
            create a larger long-term cost.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-293" data-mp="1nwnpf0nxpx22">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-294" data-mp="18ylgeay4wjdf">
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-295" data-mp="q1tfkb8b8711">
            This capability matters because many of the most damaging mistakes
            in life, institutions, and government do not come from pure
            ignorance. They come from narrow thinking that focuses only on the
            immediate, the visible, or the emotionally satisfying, while
            ignoring what comes next. Consequential thinking helps people move
            beyond impulse and toward more grounded, forward-looking judgment.
          </p>
        </motion.div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-296" data-mp="yv6fvy8peax5">
        <div className="max-w-4xl mx-auto" data-id="element-297" data-mp="3ojz1c051cazx">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-298" data-mp="3ol0dy91p95jr">
            <div className="flex items-center gap-4 mb-6" data-id="element-299" data-mp="29097vd161m8f">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-300" data-mp="28yobriz7dv8g">
                <Target size={24} data-id="element-301" data-mp="2dtkdxby5lyza" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-302" data-mp="2ipj1ixbnrcyn">
                Why It Matters
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-303" data-mp="2mu0ikoqmvc5q">
              Consequential thinking is central to responsibility. It shapes how
              people plan, how they judge risk, how they handle incentives, and
              how they make choices that affect other people, future
              opportunities, and larger systems. Weak consequential thinking
              leads to impulsive behavior, poor planning, preventable tradeoffs,
              and decisions that feel good in the moment but create instability
              over time. Strong consequential thinking improves judgment under
              uncertainty and supports better long-range outcomes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Strong Consequential Thinking Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-304" data-mp="2xqoektsmkdf4">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-305" data-mp="2a36k2qhz718g">
          <div className="flex items-center gap-4 mb-6" data-id="element-306" data-mp="2e2ffdzsnz764">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-307" data-mp="1qdw7hldj0wek">
              <CheckCircle size={24} data-id="element-308" data-mp="1mckpo20w9fgx" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-309" data-mp="1c9ffje0f7g6v">
              What Strong Consequential Thinking Looks Like
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-310" data-mp="1h1goapydqos5">
            People with strong consequential thinking ask what happens next,
            what side effects may follow, what risks may emerge later, and how
            today's decisions constrain tomorrow's options. They understand that
            problems often spread through systems rather than remaining
            isolated. They are more likely to think in timelines, tradeoffs, and
            chain reactions rather than immediate gratification or symbolic
            gestures.
          </p>
        </motion.div>
      </section>

      {/* What Weak Consequential Thinking Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-311" data-mp="9mar7n3gklqw">
        <div className="max-w-4xl mx-auto" data-id="element-312" data-mp="em6nt19ttawf">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-313" data-mp="emyj0azih6m6">
            <div className="flex items-center gap-4 mb-6" data-id="element-314" data-mp="22jpj7hz3qm95">
              <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-315" data-mp="22j62u5p3o5x8">
                <XCircle size={24} data-id="element-316" data-mp="1xi8o633atu0h" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-317" data-mp="1thonipjio89l">
                What Weak Consequential Thinking Looks Like
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-318" data-mp="1jktu2l23oy26">
              Weak consequential thinking often appears as impulsivity,
              short-termism, simplistic problem solving, and failure to
              anticipate foreseeable outcomes. It may show up in personal life
              as poor planning, in institutions as reactive policy, and in
              leadership as decisions that generate unintended consequences.
              People weak in this area may focus heavily on intentions, slogans,
              or emotional relief while neglecting what their choices actually
              produce.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Can Be Strengthened */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-319" data-mp="32kjk2axwdjfo">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-320" data-mp="37ht9vrp77x41">
          <div className="flex items-center gap-4 mb-6" data-id="element-321" data-mp="rq5bdrujdwj4">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-322" data-mp="rtjhnb6bmpvu">
              <TrendingUp size={24} data-id="element-323" data-mp="1hu2jr972vjui" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-324" data-mp="1huvflf4i0oxv">
              How It Can Be Strengthened
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-325" data-mp="1rjvkff5omejz">
            Consequential thinking can be developed through scenario planning,
            systems thinking, long-range goal setting, case studies in
            unintended consequences, structured reflection, and decision review.
            It improves when people are trained to ask not only whether an
            action feels right, but what it will likely do over time. In the
            SyNeural framework, it is treated as a critical capability for
            adulthood, institutional reliability, and leadership readiness.
          </p>
        </motion.div>
      </section>
    </div>;
}